import React from "react";

/**
 * Catches render crashes anywhere below it in the tree so the user never
 * sees a bare white screen. Logs the error to the console (and to a
 * window-level hook if present) so we can diagnose the root cause from
 * the user's browser logs.
 */
export default class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { error: null, info: null };
    }

    static getDerivedStateFromError(error) {
        return { error };
    }

    componentDidCatch(error, info) {
        // Make the crash visible in console + window for live debugging
        // eslint-disable-next-line no-console
        console.error("[ErrorBoundary]", error, info);
        try {
            window.__lastReactError = { error: String(error), stack: error?.stack, info };
        } catch (_) { /* ignore */ }
        this.setState({ info });
    }

    handleReset = () => {
        this.setState({ error: null, info: null });
        try {
            window.location.reload();
        } catch (_) { /* ignore */ }
    };

    handleHome = () => {
        try {
            window.location.href = "/dashboard";
        } catch (_) { /* ignore */ }
    };

    render() {
        if (!this.state.error) return this.props.children;

        const detail = (this.state.error && (this.state.error.message || String(this.state.error))) || "Erreur inconnue";

        return (
            <div
                className="min-h-screen flex items-center justify-center bg-paper px-6"
                data-testid="error-boundary-fallback"
            >
                <div className="max-w-lg w-full panel p-8 sm:p-10 text-center">
                    <div className="label-eyebrow mb-2 text-orange">Oups…</div>
                    <h1 className="h-display text-2xl sm:text-3xl font-semibold mb-3">
                        Quelque chose s'est mal passé.
                    </h1>
                    <p className="text-sm text-muted2 mb-6">
                        L'application a rencontré une erreur inattendue. Vos données sont en sécurité — réessayez ou retournez à l'accueil.
                    </p>
                    <details className="text-left text-xs text-muted2 mb-6 bg-surface p-3 border border-rule">
                        <summary className="cursor-pointer font-mono">Détails techniques</summary>
                        <pre className="whitespace-pre-wrap break-words mt-2">{detail}</pre>
                    </details>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <button
                            onClick={this.handleReset}
                            className="btn-primary"
                            data-testid="error-boundary-reload-button"
                        >
                            Recharger la page
                        </button>
                        <button
                            onClick={this.handleHome}
                            className="btn-outline"
                            data-testid="error-boundary-home-button"
                        >
                            Retour à l'accueil
                        </button>
                    </div>
                </div>
            </div>
        );
    }
}
